import React, { useState, useEffect, createRef, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  Column,
  Paging,
  SearchPanel,
  Sorting,
  FilterPanel,
  HeaderFilter,
  FilterBuilder,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { columnSchema } from './gridConfig';
import { createExploreSystemView, appNotify, setIsRedirectToExplore } from 'store/actions';
import { importModuleStateSelector } from 'store/selectors';
import { dataGridExtension } from 'components/devExtreme';
import DataGrid from 'components/devExtreme/DataGrid';
import CustomColumnChooser from 'components/customColumnChooser';
import { sendClientLog } from 'services/logService';
import { push } from 'connected-react-router';
import { Constants } from 'utilities/constants'
import { GetAsgPropIdsWithImportId } from 'api/revue/import'
import { postWithToken } from 'api/network';
import isPlainObject from 'lodash/isPlainObject';

const VIEW_EXPLORE = 'View Explore';

const styles = theme => ({
  gridPaper: {
    padding: '5px 8px 8px',
    overflow: 'hidden',
    width: '100%'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    // width: '66%'
  },
  title: {
    margin: '5px 0 8px 0'
  },
  refreshButton: {
    marginLeft: 8
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  flexContainer: {
    display: 'flex'
  }
});

const gridRef = createRef();

const ID = 'id';
const VIEW_ON_EXPLORER = 'View On Explore';
const API_URL = '/api/explore/import/history';

const ImportPropertyHistoryGrid = ({
  classes,
  isLoading,
  push,
  createExploreSystemView,
  appNotify,
  importHistoryRefresh,
  setIsRedirectToExplore,
 }) => {
  const [dataSource, setDataSource] = useState(null);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);

  useEffect(() => {
    createDataSource();
  }, [importHistoryRefresh]);

  const createDataSource = function (){
    const store = new CustomStore({
      key: ID,
      load: function(loadOptions) {
        if (isPlainObject(loadOptions.group)) {
          loadOptions.group = [loadOptions.group];
        }

        let params = {};

        dataGridExtension.customStoreRequestParams.forEach(i => {
          if (i in loadOptions && dataGridExtension.isNotEmpty(loadOptions[i])) {
            params[i]= loadOptions[i];
          }
        });

        return postWithToken(API_URL, params)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
              totalCount: data.totalCount,
            };
          })
          .catch((error) => {
            appNotify(`Failed to fetch Import History: ${error.message}`, 'error');
            return { data: [], totalCount: 0 };
          })
      },
    });

    setDataSource(store);
  }

  const getViewExploreColumnRender = cell => {
    if (cell.data.completedOn) {
      return <div><Button size="small" color="primary" onClick={() => handleRedirectToExplore(cell.data)}>{VIEW_ON_EXPLORER}</Button></div>;

    }
    return null;
  }

  function handleRedirectToExplore(importRecord) {
    GetAsgPropIdsWithImportId(importRecord.id, Constants.Module.EXPLORE).then(asgPropIDs => {
      // construct filterValue
      const filterValue = ['asgPropID', 'in', asgPropIDs];
      // construct filterExpression to display on UI
      const filterExpression = [];
      asgPropIDs.forEach(id => {
        filterExpression.push(['asgPropID', '=', id]);
        filterExpression.push('or');
      });
      filterExpression.pop();
      const newFilter = { filterValue, filterExpression };
      createExploreSystemView(`Property Import '${importRecord.name}'`, newFilter);
      push(Constants.Routes.EXPLORE);
    }).catch(error => {
      appNotify('Failed to redirect to explorer: ' + error.message, 'error');
      console.log(error.message);
    });
  }

  const refreshButtonClick = () => {
    createDataSource();
    sendClientLog('Info', 'Property Import History - User started data fetch - refresh button');
  }

  const onToolbarPreparing = e => {
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'columnchooser',
        elementAttr: {
          id: 'importPropertyHistoryGridColumnChooserBtn'
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  const columns = useMemo(() => {
    const columnList = columnSchema.map(({
      field,
      displayName,
      width,
      type,
      format,
      visible,
      sortIndex,
      sortOrder
    }) => {
      const headerDataSource =  dataGridExtension.createHeaderDataSource(
        API_URL,
        type,
        field,
        gridRef
      );

      return (
        <Column
          key={field}
          dataField={field}
          caption={displayName}
          minWidth={width}
          dataType={type}
          format={format}
          visible={visible !== false}
          sortIndex={sortIndex}
          sortOrder={sortOrder}
          calculateFilterExpression={calculateFilterExpression}
        >
          <HeaderFilter dataSource={headerDataSource} groupInterval={null} />
        </Column>
      )
    });

    columnList.push(
      <Column
        key={VIEW_EXPLORE}
        dataField="viewExplore"
        name={VIEW_EXPLORE}
        caption={VIEW_EXPLORE}
        minWidth={40}
        dataType="string"
        visible={true}
        cellRender={getViewExploreColumnRender}
        allowFiltering={false}
        allowSorting={false}
      />
    );

    return columnList;
  }, [dataSource]);

  // solution from devextreme support team https://supportcenter.devexpress.com/ticket/details/t998890/datagrid-column-headerfilter-customization
  function calculateFilterExpression(
    filterValue,
    selectedFilterOperation,
    target
  ) {
    if (target === 'headerFilter') {
      return [this.dataField, selectedFilterOperation, filterValue];
    }

    return this.defaultCalculateFilterExpression.apply(this, arguments);
  }

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <div className={classes.flexContainer}>
          <Typography variant='h5' className={classes.title}>Property Import History</Typography>
          <Tooltip title='Refresh Data'>
            <span>
              <IconButton disabled={isLoading} onClick={refreshButtonClick} className={classes.refreshButton}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      <Paper className={classes.gridPaper}>
        <DataGrid
          id='importPropertyHistoryGrid'
          ref={gridRef}
          persistState
          dataSource={dataSource}
          focusedRowEnabled={false}
          height={'calc(95vh - 140px)'}
          onToolbarPreparing={onToolbarPreparing}
          remoteOperations={true}
          enableTooltips
          tooltips={columnSchema}
        >
          <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
          <Sorting mode={'multiple'} />
          <FilterPanel visible={true} />
          <FilterBuilder fields={dataGridExtension.sortFields(columnSchema)}>
            {dataGridExtension.filterBuilderCustomOperations}
          </FilterBuilder>
          <Paging
            defaultPageSize={100}
            enabled={true}
          />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
          {columns}
          <Column width='auto' showInColumnChooser={false} visibleIndex={9999} />
        </DataGrid>
        <CustomColumnChooser
          container="#importPropertyHistoryGrid"
          visible={isColumnChooserVisible}
          onHiding={() => setIsColumnChooserVisible(false)}
          columns={columns}
          gridRef={gridRef}
        />
      </Paper>
    </div>
  )
}

const mapStateToProps = state => {
  const exploreState = importModuleStateSelector(state, Constants.Module.EXPLORE);
  return {
    isLoading: exploreState.importLoadingOverlayState.isLoading,
    importHistoryRefresh: exploreState.importHistoryRefresh
  }
}
const mapDispatchToProps = {
  push,
  createExploreSystemView,
  setIsRedirectToExplore,
  appNotify
}
export default compose(withStyles(styles), connect(mapStateToProps, mapDispatchToProps))(ImportPropertyHistoryGrid);
